/**
 * Calculator composable
 * Manages local reactive state for the public inheritance calculator
 * Handles sessionStorage persistence for calculator → case creation flow
 */
import { reactive, ref, computed } from 'vue'
import { getHeirsForGender } from '../config/heirCategories.js'

const SESSION_KEY = 'faraid_calculator_state'

export function useCalculator() {
  const deceasedGender = ref('male')
  const school = ref('hanafi')
  const estateValue = ref(null)
  const funeralExpenses = ref(null)
  const debts = ref(null)
  const unpaidMahr = ref(null)
  const wasiyyahAmount = ref(null)
  const result = ref(null)
  const isCalculating = ref(false)

  // Heir counts keyed by category
  const heirCounts = reactive({})

  // Get available heirs based on deceased gender
  const availableHeirs = computed(() => getHeirsForGender(deceasedGender.value))

  // Build heirs array for API request (only non-zero counts)
  const heirsPayload = computed(() => {
    return Object.entries(heirCounts)
      .filter(([, count]) => count > 0)
      .map(([category, count]) => ({ category, count }))
  })

  const hasHeirs = computed(() => heirsPayload.value.length > 0)

  const hasEstate = computed(() => estateValue.value && estateValue.value > 0)

  // Build the full calculate request
  const calculatePayload = computed(() => {
    const payload = {
      deceased_gender: deceasedGender.value,
      school: school.value,
      heirs: heirsPayload.value,
    }
    if (estateValue.value) payload.estate_value = Number(estateValue.value)
    if (funeralExpenses.value) payload.funeral_expenses = Number(funeralExpenses.value)
    if (debts.value) payload.debts = Number(debts.value)
    if (unpaidMahr.value) payload.unpaid_mahr = Number(unpaidMahr.value)
    if (wasiyyahAmount.value) payload.wasiyyah_amount = Number(wasiyyahAmount.value)
    return payload
  })

  // Heir count operations
  const setHeirCount = (category, count) => {
    if (count <= 0) {
      delete heirCounts[category]
    } else {
      heirCounts[category] = count
    }
  }

  const incrementHeir = (category, max) => {
    const current = heirCounts[category] || 0
    if (max === null || current < max) {
      heirCounts[category] = current + 1
    }
  }

  const decrementHeir = (category) => {
    const current = heirCounts[category] || 0
    if (current > 1) {
      heirCounts[category] = current - 1
    } else {
      delete heirCounts[category]
    }
  }

  const toggleHeir = (category) => {
    if (heirCounts[category]) {
      delete heirCounts[category]
    } else {
      heirCounts[category] = 1
    }
  }

  // Reset all state
  const reset = () => {
    deceasedGender.value = 'male'
    school.value = 'hanafi'
    estateValue.value = null
    funeralExpenses.value = null
    debts.value = null
    unpaidMahr.value = null
    wasiyyahAmount.value = null
    result.value = null
    Object.keys(heirCounts).forEach((key) => delete heirCounts[key])
  }

  // Clear heirs when deceased gender changes (some heirs are gender-specific)
  const setDeceasedGender = (gender) => {
    deceasedGender.value = gender
    // Clear gender-specific heirs
    Object.keys(heirCounts).forEach((key) => delete heirCounts[key])
    result.value = null
  }

  // Save to sessionStorage for calculator → case creation flow
  const saveToSession = () => {
    const state = {
      deceased_gender: deceasedGender.value,
      school: school.value,
      heirs: heirsPayload.value,
      estate_value: estateValue.value,
      funeral_expenses: funeralExpenses.value,
      debts: debts.value,
      unpaid_mahr: unpaidMahr.value,
      wasiyyah_amount: wasiyyahAmount.value,
      result: result.value,
    }
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state))
  }

  // Load from sessionStorage (used by CaseFormView)
  const loadFromSession = () => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY)
      if (!raw) return false
      const state = JSON.parse(raw)

      deceasedGender.value = state.deceased_gender || 'male'
      school.value = state.school || 'hanafi'
      estateValue.value = state.estate_value || null
      funeralExpenses.value = state.funeral_expenses || null
      debts.value = state.debts || null
      unpaidMahr.value = state.unpaid_mahr || null
      wasiyyahAmount.value = state.wasiyyah_amount || null
      result.value = state.result || null

      // Restore heir counts
      Object.keys(heirCounts).forEach((key) => delete heirCounts[key])
      if (state.heirs) {
        state.heirs.forEach(({ category, count }) => {
          heirCounts[category] = count
        })
      }
      return true
    } catch {
      return false
    }
  }

  const clearSession = () => {
    sessionStorage.removeItem(SESSION_KEY)
  }

  return {
    // State
    deceasedGender,
    school,
    estateValue,
    funeralExpenses,
    debts,
    unpaidMahr,
    wasiyyahAmount,
    result,
    isCalculating,
    heirCounts,
    // Computed
    availableHeirs,
    heirsPayload,
    hasHeirs,
    hasEstate,
    calculatePayload,
    // Methods
    setHeirCount,
    incrementHeir,
    decrementHeir,
    toggleHeir,
    setDeceasedGender,
    reset,
    saveToSession,
    loadFromSession,
    clearSession,
  }
}
